/**
 * IMS Dashboard — Invoice Management System for GSTR-2B.
 * Reconciles imported 2B against local purchase bills into 5 buckets and
 * allows accept / reject / pending on every row, mirroring the GST portal.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Upload, RefreshCw, CheckCircle2, XCircle, Circle, PlusCircle, Send,
  ShieldAlert, Repeat, Sparkles,
} from "lucide-react";
import GstinSelector from "@/components/gst/GstinSelector";

const BUCKETS = [
  { key: "matched", label: "Matched", icon: CheckCircle2, tone: "text-emerald-500" },
  { key: "only_in_books", label: "Only in Books", icon: XCircle, tone: "text-red-500" },
  { key: "only_in_2b", label: "Only in 2B", icon: PlusCircle, tone: "text-sky-500" },
  { key: "ineligible_in_2b", label: "Ineligible in 2B", icon: ShieldAlert, tone: "text-amber-500" },
  { key: "rcm_in_2b", label: "RCM in 2B", icon: Repeat, tone: "text-fuchsia-500" },
];

export default function ImsDashboard() {
  const now = new Date();
  const [period, setPeriod] = useState(
    String(now.getMonth() + 1).padStart(2, "0") + now.getFullYear()
  );
  const [gstin, setGstin] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("matched");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (gstin?.gstin) params.gstin = gstin.gstin;
      const r = await api.get(`/ims/dashboard/${period}`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uploadRef = React.useRef();
  const importJson = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await api.post(`/ims/import-2b/${period}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${JSON.stringify(r.data.counts)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImporting(false);
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const doAction = async (row, action) => {
    const key = `${row.supplier_gstin}/${row.inum}`;
    try {
      await api.post(`/ims/action/${key}`, { action });
      toast.success(`${action}: ${key}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const bookMissing = async (row) => {
    const key = `${row.supplier_gstin}/${row.inum}`;
    if (!window.confirm(`Auto-draft a purchase bill from this 2B row?`)) return;
    try {
      const r = await api.post(`/ims/book/${key}`, {});
      toast.success(`Draft bill created (id: ${r.data.bill_id.slice(0, 8)}). Complete line items in Purchases → Bills.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Book failed");
    }
  };

  const syncToPortal = async () => {
    if (!window.confirm("Push all IMS decisions for this period to the GST portal via Sandbox?")) return;
    try {
      const r = await api.post(`/ims/sync-to-portal/${period}`);
      toast.success(`Synced ${r.data.actions_pushed} actions to portal`);
    } catch (e) {
      const d = e?.response?.data?.detail || "Sync failed";
      if (String(d).toLowerCase().includes("insufficient")) {
        toast.error("Sandbox 'GST · Returns Read' scope not unlocked yet. Email sales@sandbox.co.in.", { duration: 8000 });
      } else { toast.error(d); }
    }
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const rows = (data && data[tab]) || [];

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="ims-dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · IMS · GSTR-2B RECON</div>
          <h1 className="text-3xl font-bold mt-1 font-heading flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-indigo-400" /> IMS Dashboard
          </h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Import your GSTR-2B JSON (downloaded from the portal), reconcile against
            purchase bills, then Accept / Reject / Pending each row before syncing back.
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <GstinSelector value={gstin} onChange={setGstin} testid="ims-gstin" />
          <Input value={period} onChange={(e) => setPeriod(e.target.value)}
            placeholder="MMYYYY" className="w-32 rounded-sm h-9 font-mono" data-testid="ims-period" />
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-sm gap-1.5" data-testid="ims-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <input type="file" accept=".json" ref={uploadRef}
            onChange={importJson} className="hidden" data-testid="ims-file" />
          <Button onClick={() => uploadRef.current?.click()} disabled={importing}
            className="rounded-sm bg-sky-600 hover:bg-sky-700 text-white gap-1.5"
            data-testid="ims-import">
            <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import 2B JSON"}
          </Button>
          <Button onClick={syncToPortal}
            className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
            data-testid="ims-sync">
            <Send className="w-4 h-4" /> Sync to Portal
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {BUCKETS.map(({ key, label, icon: Icon, tone }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`card-sharp p-3 text-left transition-all ${
                  tab === key ? "ring-2 ring-accent" : "hover:bg-white/5"}`}
                data-testid={`ims-bucket-${key}`}>
                <div className="flex items-center justify-between">
                  <Icon className={`w-4 h-4 ${tone}`} />
                  <span className={`text-2xl font-bold font-mono tabular-nums ${tone}`}>
                    {data.counts[key] || 0}
                  </span>
                </div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest mt-1">{label}</div>
              </button>
            ))}
          </div>

          <div className="card-sharp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Supplier / GSTIN</th>
                  <th className="p-2 text-left">Inv No</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-right">2B Taxable</th>
                  <th className="p-2 text-right">Books Taxable</th>
                  <th className="p-2 text-right">Delta</th>
                  <th className="p-2 text-left">Doc Type</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-white/5">
                    <td className="p-2">
                      <div className="font-medium">{r.supplier_name || "-"}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{r.supplier_gstin}</div>
                    </td>
                    <td className="p-2 font-mono text-xs">{r.inum || r.vendor_bill_number}</td>
                    <td className="p-2 text-muted-foreground text-xs">{r.idt || r.date}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{inr(r.taxable)}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{inr(r.books_taxable)}</td>
                    <td className={`p-2 text-right font-mono tabular-nums ${
                      (r.delta_taxable || 0) === 0 ? "text-muted-foreground" :
                      Math.abs(r.delta_taxable || 0) < 1 ? "text-emerald-500" : "text-amber-500"}`}>
                      {r.delta_taxable != null ? inr(r.delta_taxable) : "—"}
                    </td>
                    <td className="p-2 text-xs font-mono">{r.doc_type || "-"}</td>
                    <td className="p-2">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${
                        r.status === "accept" ? "bg-emerald-500/20 text-emerald-500" :
                        r.status === "reject" ? "bg-red-500/20 text-red-500" :
                        "bg-white/10 text-white/60"}`}>
                        {r.status || "pending"}
                      </span>
                    </td>
                    <td className="p-2 flex gap-1">
                      {tab !== "only_in_books" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => doAction(r, "accept")}
                            data-testid={`ims-accept-${idx}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => doAction(r, "reject")}
                            data-testid={`ims-reject-${idx}`}>
                            <XCircle className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => doAction(r, "pending")}
                            data-testid={`ims-pending-${idx}`}>
                            <Circle className="w-3.5 h-3.5 text-white/60" />
                          </Button>
                        </>
                      )}
                      {tab === "only_in_2b" && (
                        <Button size="sm" className="h-7 px-2 gap-1 bg-sky-600 hover:bg-sky-700 text-white"
                          onClick={() => bookMissing(r)} data-testid={`ims-book-${idx}`}>
                          <PlusCircle className="w-3.5 h-3.5" /> Book
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center text-muted-foreground text-sm">
                    Nothing here. Upload a GSTR-2B JSON (top-right) to populate the dashboard.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
